import { useEffect, useMemo, useRef, useState } from 'react';
import { Tag, Vault } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Button } from '../ui/Button';
import { categoryLabel, categoryMeta } from '../accounts/assetCategories';
import { CategoryFormDialog } from '../categories/CategoryFormDialog';
import { categoryIdsMatch, mergeCategoryIntoList, splitCategoriesForPreview } from '../categories/categoryPickerUtils';
import { QuickCreateDateChips } from '../quickCreate/QuickCreateDateChips';
import { QuickCreateField } from '../quickCreate/QuickCreateField';
import { QuickCreateFieldGroup } from '../quickCreate/QuickCreateFieldGroup';
import { QuickCreateFormSkeleton } from '../quickCreate/QuickCreateFormSkeleton';
import { QuickCreateHeroAmount } from '../quickCreate/QuickCreateHeroAmount';
import { QuickCreateInlineCreateField } from '../quickCreate/QuickCreateInlineCreateField';
import { QuickCreatePickerFooter } from '../quickCreate/QuickCreatePickerFooter';
import { QuickCreatePickerRow } from '../quickCreate/QuickCreatePickerRow';
import {
  AccountPickerSheet,
  CategoryPickerSheet,
  DatePickerSheet,
} from '../quickCreate/QuickCreatePickers';
import { VaultDepositHints } from './VaultDepositHints';
import { WizardErrorCallout } from '../wizard/WizardErrorCallout';
import { formatCategoryLimitLabel, payloadFromCategoryAllocation } from '../../utils/categoryAllocation';
import { QUICK_ALLOCATION_OPTIONS } from '../../utils/quickCreateAllocation';
import { parsePtNumber } from '../../utils/money';
import {
  decimalToPtInput,
  filterActivePots,
  findPotById,
  potCycleDepositAmount,
  validatePotDepositAmount,
} from '../../utils/quickExpensePots';
import { todayISO } from '../../utils/quickCreateDates';
import pt from '../../locales/pt';
import {
  useActiveCategoriesQuery,
  useCreateCategoryMutation,
  useCreateExpenseMutation,
  useSystemPotCategoryQuery,
} from '../../features/expenses/queries';
import { useActiveAccountsQuery } from '../../features/accounts/queries';
import { useExpenseSheet } from '../../features/expenses/SheetProvider';
import { usePotsQuery } from '../../features/pots/queries';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { useGuardedBottomSheetClose } from '../../hooks/useGuardedBottomSheetClose';

const TITLE_CLASS_NAME = 'type-title-page text-card-foreground';

export function QuickExpenseSheet() {
  const { isOpen, setOpen, presetCategoryId, presetSavingsPotId, close } = useExpenseSheet();
  const { currentCycle } = useSelfieCycle();
  const minDate = useMemo(() => currentCycle?.start ?? todayISO(), [currentCycle]);
  const maxDate = useMemo(() => currentCycle?.end ?? todayISO(), [currentCycle]);
  const {
    data: activeCategories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useActiveCategoriesQuery({ enabled: isOpen });
  const { data: accounts = [] } = useActiveAccountsQuery({ enabled: isOpen });
  const { data: systemPotCategory } = useSystemPotCategoryQuery({ enabled: isOpen });
  const mutation = useCreateExpenseMutation();
  const createCategory = useCreateCategoryMutation();

  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState(presetCategoryId ?? null);
  const [accountId, setAccountId] = useState(null);
  const [date, setDate] = useState(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [savingsPotId, setSavingsPotId] = useState('');
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [pinnedCategory, setPinnedCategory] = useState(null);
  const [firstCategoryExpanded, setFirstCategoryExpanded] = useState(false);
  const [firstCategoryName, setFirstCategoryName] = useState('');
  const [firstCategoryAllocation, setFirstCategoryAllocation] = useState('essential');
  const [firstCategoryError, setFirstCategoryError] = useState(null);

  const { data: pots = [] } = usePotsQuery(undefined, { enabled: isOpen });
  const activePots = useMemo(() => filterActivePots(pots), [pots]);
  const isDirectPotDeposit = Boolean(presetSavingsPotId);
  const selectedPot = useMemo(
    () => findPotById(activePots, savingsPotId),
    [activePots, savingsPotId],
  );
  const isPotDeposit = Boolean(selectedPot);

  const openedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;

    setCategoryId(presetCategoryId ?? null);
    setDescription('');
    setDate(null);
    setError(null);
    setCategoryFormOpen(false);
    setCategoryPickerOpen(false);
    setAccountPickerOpen(false);
    setPinnedCategory(null);
    setFirstCategoryExpanded(false);
    setFirstCategoryName('');
    setFirstCategoryAllocation('essential');
    setFirstCategoryError(null);
    setSavingsPotId('');
    setAmount('');
    setAccountId(null);
  }, [isOpen, presetCategoryId]);

  useEffect(() => {
    if (!isOpen || !presetSavingsPotId) return;
    const presetPot = findPotById(activePots, presetSavingsPotId);
    if (!presetPot) return;
    setSavingsPotId(String(presetPot.id));
    setAmount(decimalToPtInput(potCycleDepositAmount(presetPot)));
    setCategoryId(null);
  }, [isOpen, presetSavingsPotId, activePots]);

  const userCategories = useMemo(
    () => activeCategories.filter((category) => !category.is_system_pot),
    [activeCategories],
  );

  const needsFirstCategory =
    userCategories.length === 0 && !pinnedCategory && !isPotDeposit && !isDirectPotDeposit;

  const pickerCategories = useMemo(() => {
    let list = [...userCategories];
    if (pinnedCategory) list = mergeCategoryIntoList(list, pinnedCategory);
    if (categoryId) {
      const selected = userCategories.find((c) => categoryIdsMatch(c.id, categoryId));
      if (selected) list = mergeCategoryIntoList(list, selected);
      else if (pinnedCategory && categoryIdsMatch(pinnedCategory.id, categoryId)) {
        list = mergeCategoryIntoList(list, pinnedCategory);
      }
    }
    return list;
  }, [userCategories, pinnedCategory, categoryId]);

  const CATEGORY_ROW_CAP = 3;
  const { preview: categoryPreviewAll, extra: categoryExtraAll } = useMemo(
    () => splitCategoriesForPreview(pickerCategories, categoryId, CATEGORY_ROW_CAP),
    [pickerCategories, categoryId],
  );
  const categoryPreview = categoryPreviewAll.slice(0, CATEGORY_ROW_CAP);
  const categoryExtra = useMemo(
    () => [...categoryPreviewAll.slice(CATEGORY_ROW_CAP), ...categoryExtraAll],
    [categoryPreviewAll, categoryExtraAll],
  );

  const ACCOUNT_ROW_CAP = 3;
  const orderedAccounts = useMemo(() => {
    const idx = accounts.findIndex((account) => String(account.id) === String(accountId));
    if (idx <= 0) return accounts;
    const copy = [...accounts];
    const [selected] = copy.splice(idx, 1);
    copy.unshift(selected);
    return copy;
  }, [accounts, accountId]);
  const accountPreview = orderedAccounts.slice(0, ACCOUNT_ROW_CAP);
  const accountExtra = orderedAccounts.slice(ACCOUNT_ROW_CAP);

  const numericAmount = parsePtNumber(amount);
  const potDepositValidation = isPotDeposit
    ? validatePotDepositAmount(selectedPot, amount)
    : { valid: false };
  const canSubmit =
    !mutation.isPending &&
    (isPotDeposit
      ? potDepositValidation.valid
      : Boolean(numericAmount && numericAmount > 0 && categoryId && date && accountId));

  const handleCreateCategory = async (values) => {
    try {
      const created = await createCategory.mutateAsync(values);
      if (created?.id) {
        setPinnedCategory(created);
        setCategoryId(String(created.id));
        setSavingsPotId('');
        setFirstCategoryExpanded(false);
        setFirstCategoryName('');
        setFirstCategoryError(null);
      }
      setCategoryFormOpen(false);
      toast.success(pt.quickExpense.categoryCreatedToast);
      return created;
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.detail || err?.name || pt.auth.errors.generic;
      toast.error(message);
      throw err;
    }
  };

  const handleInlineCreateCategory = async () => {
    if (!firstCategoryName.trim() || createCategory.isPending) return;
    setFirstCategoryError(null);
    try {
      await handleCreateCategory({
        name: firstCategoryName.trim(),
        ...payloadFromCategoryAllocation(firstCategoryAllocation),
        color: '#7C9E6F',
      });
    } catch (err) {
      setFirstCategoryError(
        typeof err === 'string'
          ? err
          : err?.detail || err?.name || pt.auth.errors.generic,
      );
    }
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      const payload = {
        amount: numericAmount.toFixed(2),
        occurred_on: date,
        description,
        savings_pot: savingsPotId || null,
        account: accountId || null,
      };
      if (isPotDeposit) {
        if (systemPotCategory?.id) {
          payload.category = systemPotCategory.id;
        }
      } else {
        payload.category = categoryId;
      }
      await mutation.mutateAsync(payload);
      toast.success(
        isPotDeposit ? pt.pots.contribute.success : pt.quickExpense.successToast,
      );
      close();
    } catch (err) {
      setError(
        typeof err === 'string'
          ? err
          : err?.detail || err?.amount || pt.auth.errors.generic,
      );
    }
  };

  const sheetTitle =
    isDirectPotDeposit && selectedPot
      ? pt.quickExpense.potDepositDirectTitle
      : pt.quickCreate.expense.title;

  const submitLabel = isPotDeposit
    ? pt.quickExpense.potDepositSubmit
    : pt.quickCreate.expense.submit;

  const amountLabel =
    isPotDeposit || isDirectPotDeposit
      ? pt.quickExpense.potDepositAmountLabel
      : pt.quickCreate.expense.amountLabel;

  const { handleOpenChange, blockDismissProps, wrapChildOpenChange } = useGuardedBottomSheetClose({
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    childOpen: [categoryPickerOpen, accountPickerOpen, categoryFormOpen, datePickerOpen],
  });

  return (
    <>
      <BottomSheet open={isOpen} onOpenChange={handleOpenChange}>
        <BottomSheetContent
          title={sheetTitle}
          layout="bottom"
          headerAlign="start"
          titleClassName={TITLE_CLASS_NAME}
          {...blockDismissProps}
          footer={
            !categoriesLoading && !categoriesError ? (
              <Button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                loading={mutation.isPending}
                className="w-full justify-center"
              >
                {mutation.isPending ? pt.common.saving : submitLabel}
              </Button>
            ) : null
          }
        >
          {categoriesLoading ? (
            <QuickCreateFormSkeleton />
          ) : categoriesError ? (
            <div className="flex flex-col items-center gap-space-3 py-6 text-center">
              <p className="type-body-sm text-muted-foreground">{pt.auth.errors.generic}</p>
              <Button type="button" variant="secondary" onClick={() => refetchCategories()}>
                {pt.common.retry}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-space-4 pb-2">
              {isDirectPotDeposit && selectedPot ? (
                <div className="flex w-full items-center gap-space-3 rounded-ds-lg border border-sage-border bg-sage-tint px-space-3 py-space-2-5">
                  <Vault className="h-4 w-4 shrink-0 text-sage-on-tint" aria-hidden />
                  <p className="type-body min-w-0 flex-1 truncate font-semibold text-sage-on-tint">
                    {pt.quickExpense.potDepositLabel} · {selectedPot.name}
                  </p>
                </div>
              ) : null}

              <QuickCreateHeroAmount
                label={amountLabel}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />

              {isPotDeposit && selectedPot ? (
                <VaultDepositHints
                  selectedPot={selectedPot}
                  amount={amount}
                  validation={potDepositValidation}
                  setAmount={setAmount}
                />
              ) : null}

              {!isDirectPotDeposit && !isPotDeposit ? (
                <QuickCreateField label={pt.quickCreate.categoryLabel}>
                  {needsFirstCategory ? (
                    <QuickCreateInlineCreateField
                      expanded={firstCategoryExpanded}
                      onExpand={() => setFirstCategoryExpanded(true)}
                      onCancel={() => setFirstCategoryExpanded(false)}
                      title={pt.quickCreate.createFirstCategory}
                      description={pt.quickCreate.createFirstCategoryDescription}
                      nameLabel={pt.quickCreate.category.nameLabel}
                      name={firstCategoryName}
                      onNameChange={(event) => setFirstCategoryName(event.target.value)}
                      namePlaceholder={pt.quickCreate.category.namePlaceholder}
                      allocationOptions={QUICK_ALLOCATION_OPTIONS}
                      allocation={firstCategoryAllocation}
                      onAllocationChange={setFirstCategoryAllocation}
                      onCreate={handleInlineCreateCategory}
                      submitLabel={pt.quickCreate.createFirstCategoryInlineSubmit}
                      cancelLabel={pt.quickCreate.createFirstCategoryCancel}
                      isPending={createCategory.isPending}
                      error={firstCategoryError}
                    />
                  ) : (
                    <div className="flex flex-col gap-space-2">
                      {categoryPreview.map((category) => (
                        <QuickCreatePickerRow
                          key={category.id}
                          icon={Tag}
                          name={category.name}
                          subtitle={formatCategoryLimitLabel(category, pt)}
                          selected={categoryIdsMatch(category.id, categoryId)}
                          onSelect={() => {
                            setCategoryId(category.id);
                            setSavingsPotId('');
                          }}
                        />
                      ))}
                      <QuickCreatePickerFooter
                        onAdd={() => setCategoryFormOpen(true)}
                        addLabel={pt.quickCreate.newCategoryShort}
                        addAriaLabel={pt.quickCreate.newCategory}
                        onShowMore={categoryExtra.length > 0 ? () => setCategoryPickerOpen(true) : undefined}
                        moreCount={categoryExtra.length}
                        moreLabel={pt.quickCreate.moreCategoriesShort}
                      />
                    </div>
                  )}
                </QuickCreateField>
              ) : null}

              <QuickCreateDateChips
                value={date}
                onSelect={setDate}
                onOpenPicker={() => setDatePickerOpen(true)}
                min={minDate}
                max={maxDate}
                ariaLabel={pt.quickCreate.dateLabel}
              />

              <QuickCreateField label={pt.quickCreate.accountLabel}>
                <div className="flex flex-col gap-space-2">
                  {accountPreview.map((account) => {
                    const meta = categoryMeta(account.category);
                    return (
                      <QuickCreatePickerRow
                        key={account.id}
                        icon={meta.icon}
                        name={account.name}
                        subtitle={categoryLabel(account.category)}
                        selected={String(account.id) === String(accountId)}
                        onSelect={() => setAccountId(String(account.id))}
                      />
                    );
                  })}
                  <QuickCreatePickerFooter
                    onShowMore={accountExtra.length > 0 ? () => setAccountPickerOpen(true) : undefined}
                    moreCount={accountExtra.length}
                    moreLabel={pt.quickCreate.moreAccountsShort}
                  />
                </div>
              </QuickCreateField>

              <QuickCreateFieldGroup
                label={pt.quickCreate.noteLabel}
                optional={pt.quickCreate.optional}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={pt.quickCreate.notePlaceholder}
              />

              {error ? (
                <WizardErrorCallout>
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </WizardErrorCallout>
              ) : null}
            </div>
          )}
        </BottomSheetContent>
      </BottomSheet>

      <CategoryFormDialog
        isOpen={categoryFormOpen}
        onClose={() => setCategoryFormOpen(false)}
        onSubmit={handleCreateCategory}
        isPending={createCategory.isPending}
      />

      <CategoryPickerSheet
        open={categoryPickerOpen}
        onOpenChange={wrapChildOpenChange(setCategoryPickerOpen)}
        categories={categoryExtra}
        selectedId={categoryId}
        onSelect={(id) => {
          setCategoryId(id);
          setSavingsPotId('');
        }}
        onAddNew={() => setCategoryFormOpen(true)}
      />

      <AccountPickerSheet
        open={accountPickerOpen}
        onOpenChange={wrapChildOpenChange(setAccountPickerOpen)}
        accounts={accountExtra}
        selectedId={accountId}
        onSelect={setAccountId}
      />

      <DatePickerSheet
        open={datePickerOpen}
        onOpenChange={wrapChildOpenChange(setDatePickerOpen)}
        value={date}
        min={minDate}
        max={maxDate}
        onSelect={setDate}
      />
    </>
  );
}

export default QuickExpenseSheet;
